import { useCallback, useLayoutEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import type { History, Location } from "history";

/**
 * Where this tab sits among the app's own entries. The browser hides what came before the app (a link, the Auth0
 * login), so "back" is only safe while there is an earlier in-app entry to return to. Kept per tab in sessionStorage,
 * so a reload (pull-to-refresh, a phone discarding the tab) still knows what is behind it.
 */
const STORAGE_KEY = "nubrakes:in-app-history:v1";
type Trail = { entries: string[]; current: number };
let trail: Trail = { entries: [], current: 0 };

// The first entry of a direct load has no key.
const NO_KEY = "entry";
const keyOf = (location: Location) => location.key ?? NO_KEY;

const load = (): Trail | null => {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? "null");
    return saved && Array.isArray(saved.entries) && typeof saved.current === "number" ? saved : null;
  } catch {
    return null;
  }
};

const save = () => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trail));
  } catch {
    // Unstorable: a reload then falls back rather than going back, which is still safe.
  }
};

const start = (key: string): Trail => {
  const saved = key === NO_KEY ? null : load();
  const index = saved ? saved.entries.indexOf(key) : -1;
  return saved && index >= 0 ? { entries: saved.entries, current: index } : { entries: [key], current: 0 };
};

const track = (history: History) => {
  trail = start(keyOf(history.location));
  save();
  return history.listen((location, action) => {
    const key = keyOf(location);
    const { entries, current } = trail;
    if (action === "REPLACE") {
      trail = { entries: entries.map((entry, index) => (index === current ? key : entry)), current };
    } else if (action === "PUSH") {
      trail = { entries: [...entries.slice(0, current + 1), key], current: current + 1 };
    } else {
      // An entry the trail never saw: treat it as the first, so back falls back instead of leaving the app.
      const known = entries.indexOf(key);
      trail = known >= 0 ? { entries, current: known } : { entries: [key], current: 0 };
    }
    save();
  });
};

/** Mounted once under the router, before anything can navigate. */
export const useTrackInAppHistory = () => {
  const history = useHistory();
  useLayoutEffect(() => track(history), [history]);
};

export const canGoBackInApp = () => trail.current > 0;

/** Back to the previous in-app page, or to `fallback` when this page was the first one opened. */
export const useBackOr = (fallback: string) => {
  const history = useHistory();
  return useCallback(() => (canGoBackInApp() ? history.goBack() : history.replace(fallback)), [history, fallback]);
};

/** A job opened directly falls back to the Schedule rather than leaving the app. */
export const useBackOrSchedule = () => useBackOr("/");

const RETURN_JOB_PARAM = "job";

/** A link from a job's page, carrying the job so the page can fall back to it after a reload. */
export const withReturnJob = (path: string, jobId?: string | null) =>
  jobId ? `${path}?${RETURN_JOB_PARAM}=${encodeURIComponent(jobId)}` : path;

/** Contact and job history pages: back, else the job they were opened from, else the Schedule. */
export const useBackOrReturnJob = () => {
  const jobId = new URLSearchParams(useLocation().search).get(RETURN_JOB_PARAM);
  return useBackOr(jobId ? `/jobs/${encodeURIComponent(jobId)}` : "/");
};
